<div class="profile-dialog-container">
  <!-- Dialog Header -->
  <div class="dialog-header">
    <div class="header-content">
      <h2 mat-dialog-title>Profile Settings</h2>
      <p class="header-subtitle">Manage your account information and preferences</p>
    </div>
    <button mat-icon-button (click)="onCancel()" class="close-button">
      <mat-icon>close</mat-icon>
    </button>
  </div>
  
  <mat-dialog-content class="dialog-content">
    <!-- User Info Section -->
    <div class="user-profile-section">
      <div class="profile-avatar-large">
        <span class="avatar-initials">{{ user.firstName.charAt(0) }}{{ user.lastName.charAt(0) }}</span>
      </div>
      <div class="profile-user-info">
        <h3 class="user-name">{{ user.firstName }} {{ user.lastName }}</h3>
        <p class="user-email">{{ user.email }}</p>
        <div class="user-meta">
          <span class="role-badge">
            <mat-icon>verified_user</mat-icon>
            {{ user.role | titlecase }}
          </span>
          @if (user.tenant) {
            <span class="tenant-badge">
              <mat-icon>business</mat-icon>
              {{ user.tenant.name }}
            </span>
          }
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <mat-tab-group [(selectedIndex)]="activeTab" class="profile-tabs">
      
      <!-- Profile Information Tab -->
      <mat-tab>
        <ng-template mat-tab-label>
          <mat-icon>person</mat-icon>
          Profile Information
        </ng-template>
        
        <div class="tab-content">
          <form [formGroup]="profileForm" class="profile-form">
            
            <!-- Personal Information Section -->
            <div class="form-section">
              <div class="section-header">
                <mat-icon>badge</mat-icon>
                <h4>Personal Information</h4>
              </div>
              
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">First Name <span class="required">*</span></label>
                  <mat-form-field appearance="outline">
                    <input matInput formControlName="firstName" placeholder="Enter first name">
                    @if (profileForm.get('firstName')?.hasError('required')) {
                      <mat-error>First name is required</mat-error>
                    }
                    @if (profileForm.get('firstName')?.hasError('serverError')) {
                      <mat-error>{{ profileForm.get('firstName')?.getError('serverError') }}</mat-error>
                    }
                  </mat-form-field>
                </div>
                
                <div class="form-group">
                  <label class="form-label">Last Name <span class="required">*</span></label>
                  <mat-form-field appearance="outline">
                    <input matInput formControlName="lastName" placeholder="Enter last name">
                    @if (profileForm.get('lastName')?.hasError('required')) {
                      <mat-error>Last name is required</mat-error>
                    }
                    @if (profileForm.get('lastName')?.hasError('serverError')) {
                      <mat-error>{{ profileForm.get('lastName')?.getError('serverError') }}</mat-error>
                    }
                  </mat-form-field>
                </div>
              </div>
              
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">Phone Number</label>
                  <mat-form-field appearance="outline">
                    <input matInput 
                           type="tel" 
                           formControlName="phone" 
                           placeholder="Enter phone number"
                           (input)="onPhoneInput($event)"
                           pattern="[0-9]*">
                    <mat-icon matPrefix>phone</mat-icon>
                    @if (profileForm.get('phone')?.hasError('pattern')) {
                      <mat-error>Phone number must contain only numbers</mat-error>
                    }
                    @if (profileForm.get('phone')?.hasError('serverError')) {
                      <mat-error>{{ profileForm.get('phone')?.getError('serverError') }}</mat-error>
                    }
                  </mat-form-field>
                </div>
              </div>
            </div>

            <!-- Account Information Section (Read-only) -->
            <div class="form-section readonly-section">
              <div class="section-header">
                <mat-icon>lock</mat-icon>
                <h4>Account Information</h4>
              </div>
              
              <div class="readonly-fields">
                <div class="readonly-item">
                  <div class="readonly-label">
                    <mat-icon>email</mat-icon>
                    <span>Email Address</span>
                  </div>
                  <div class="readonly-value">{{ user.email }}</div>
                </div>
                
                <div class="readonly-item">
                  <div class="readonly-label">
                    <mat-icon>admin_panel_settings</mat-icon>
                    <span>Role</span>
                  </div>
                  <div class="readonly-value">{{ user.role | titlecase }}</div>
                </div>
                
                @if (user.tenant) {
                  <div class="readonly-item">
                    <div class="readonly-label">
                      <mat-icon>business</mat-icon>
                      <span>Tenant</span>
                    </div>
                    <div class="readonly-value">{{ user.tenant.name }}</div>
                  </div>
                }
              </div>
            </div>
          </form>
        </div>
      </mat-tab>

      <!-- Change Password Tab -->
      <mat-tab>
        <ng-template mat-tab-label>
          <mat-icon>lock</mat-icon>
          Change Password
        </ng-template>
        
        <div class="tab-content">
          <form [formGroup]="passwordForm" class="password-form">
            
            <div class="form-section">
              <div class="section-header">
                <mat-icon>security</mat-icon>
                <h4>Security Settings</h4>
              </div>
              
              <div class="security-notice">
                <mat-icon>info</mat-icon>
                <p>Password must be at least 6 characters long. Choose a strong password to keep your account secure.</p>
              </div>
              
              <div class="form-row">
                <div class="form-group full-width">
                  <label class="form-label">Current Password <span class="required">*</span></label>
                  <mat-form-field appearance="outline">
                    <input matInput type="password" formControlName="currentPassword" placeholder="Enter current password">
                    <mat-icon matPrefix>lock_outline</mat-icon>
                    @if (passwordForm.get('currentPassword')?.hasError('required')) {
                      <mat-error>Current password is required</mat-error>
                    }
                    @if (passwordForm.get('currentPassword')?.hasError('minlength')) {
                      <mat-error>Password must be at least 6 characters</mat-error>
                    }
                    @if (passwordForm.get('currentPassword')?.hasError('serverError')) {
                      <mat-error>{{ passwordForm.get('currentPassword')?.getError('serverError') }}</mat-error>
                    }
                  </mat-form-field>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">New Password <span class="required">*</span></label>
                  <mat-form-field appearance="outline">
                    <input matInput type="password" formControlName="newPassword" placeholder="Enter new password">
                    <mat-icon matPrefix>lock</mat-icon>
                    @if (passwordForm.get('newPassword')?.hasError('required')) {
                      <mat-error>New password is required</mat-error>
                    }
                    @if (passwordForm.get('newPassword')?.hasError('minlength')) {
                      <mat-error>Password must be at least 6 characters</mat-error>
                    }
                    @if (passwordForm.get('newPassword')?.hasError('serverError')) {
                      <mat-error>{{ passwordForm.get('newPassword')?.getError('serverError') }}</mat-error>
                    }
                  </mat-form-field>
                </div>
                
                <div class="form-group">
                  <label class="form-label">Confirm New Password <span class="required">*</span></label>
                  <mat-form-field appearance="outline">
                    <input matInput type="password" formControlName="confirmPassword" placeholder="Confirm new password">
                    <mat-icon matPrefix>lock_clock</mat-icon>
                    @if (passwordForm.get('confirmPassword')?.hasError('required')) {
                      <mat-error>Please confirm your password</mat-error>
                    }
                    @if (passwordForm.get('confirmPassword')?.hasError('passwordMismatch')) {
                      <mat-error>Passwords do not match</mat-error>
                    }
                    @if (passwordForm.get('confirmPassword')?.hasError('serverError')) {
                      <mat-error>{{ passwordForm.get('confirmPassword')?.getError('serverError') }}</mat-error>
                    }
                  </mat-form-field>
                </div>
              </div>
            </div>
          </form>
        </div>
      </mat-tab>
    </mat-tab-group>
  </mat-dialog-content>

  <!-- Dialog Actions -->
  <mat-dialog-actions class="dialog-actions">
    <button mat-raised-button class="btn-secondary" (click)="onCancel()">
      <mat-icon>close</mat-icon>
      Close
    </button>
    
    @if (activeTab === 0) {
      <button mat-raised-button 
              class="btn-primary btn-with-icon"
              (click)="onSaveProfile()"
              [disabled]="profileForm.invalid || isLoading">
        @if (isLoading) {
          <mat-spinner diameter="20"></mat-spinner>
        } @else {
          <ng-container>
            <mat-icon>save</mat-icon>
            <span>Save Profile</span>
          </ng-container>
        }
      </button>
    } @else {
      <button mat-raised-button 
              class="btn-primary btn-with-icon"
              (click)="onChangePassword()"
              [disabled]="passwordForm.invalid || isLoading">
        @if (isLoading) {
          <mat-spinner diameter="20"></mat-spinner>
        } @else {
          <ng-container>
            <mat-icon>lock</mat-icon>
            <span>Change Password</span>
          </ng-container>
        }
      </button>
    }
  </mat-dialog-actions>
</div>
